import { useChatProvidersQuery } from "@/api/queries/admin/ChatProviderQueries";
import { useEmbeddingProvidersQuery } from "@/api/queries/admin/EmbeddingProviderQueries";
import { useAuthProvidersQuery } from "@/api/queries/admin/AuthProviderQueries";
import { useSetEnabledChatProviderMutation } from "@/api/mutations/admin/ChatProviderMutations";
import { useSetEnabledEmbeddingProviderMutation } from "@/api/mutations/admin/EmbeddingProviderMutations";
import { useSetEnabledAuthProviderMutation } from "@/api/mutations/admin/AuthProviderMutations";
import { DataTableSkeleton } from "@/components/data-tables/data-table-skeleton";
import ChatProviderDataTable from "@/components/data-tables/providers/chat/ChatProviderDataTable";
import EmbeddingProviderDataTable from "@/components/data-tables/providers/embedding/EmbeddingProviderDataTable";
import AuthProviderDataTable from "@/components/data-tables/providers/auth/AuthProviderDataTable";
import ApplicationSettingsSection from "@/components/sections/ApplicationSettingsSection";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { ChatProviderDialog } from "@/components/dialogs/providers/ChatProviderDialog";
import { EmbeddingProviderDialog } from "@/components/dialogs/providers/EmbeddingProviderDialog";
import { VectorStoreProviderDialog } from "@/components/dialogs/providers/VectorStoreProviderDialog";
import { AuthProviderDialog } from "@/components/dialogs/providers/AuthProviderDialog";
import BrandingSettingsSection from "@/components/sections/BrandingSettingsSection";
import SSLSettingsSection from "@/components/sections/SSLSettingsSection";
import NotificationSettingsSection from "@/components/sections/NotificationSettingsSection";
import SmtpSettingsSection from "@/components/sections/SmtpSettingsSection";
import Personalization from "@/pages/Personalization";
import { useTranslation } from "react-i18next";

const Settings = () => {
    const { t } = useTranslation();
    const { setting } = useParams();
    
    // Dialog states
    const [chatProviderDialog, setChatProviderDialog] = useState<{
        open: boolean;
        provider: any;
        mode?: "create" | "edit";
    }>({ open: false, provider: null, mode: "edit" });

    const [embeddingProviderDialog, setEmbeddingProviderDialog] = useState<{
        open: boolean;
        provider: any;
        mode?: "create" | "edit";
    }>({ open: false, provider: null, mode: "edit" });

    const [vectorStoreProviderDialog, setVectorStoreProviderDialog] = useState<{
        open: boolean;
        provider: any;
    }>({ open: false, provider: null });

    const [authProviderDialog, setAuthProviderDialog] = useState<{
        open: boolean;
        provider: any;
    }>({ open: false, provider: null });

    // Provider queries
    const chatProvidersQuery = useChatProvidersQuery();
    const embeddingProvidersQuery = useEmbeddingProvidersQuery();
    // const vectorStoreProvidersQuery = useVectorStoreProvidersQuery();
    const authProvidersQuery = useAuthProvidersQuery();

    // Provider mutations
    const setEnabledChatProviderMutation = useSetEnabledChatProviderMutation();
    const setEnabledEmbeddingProviderMutation = useSetEnabledEmbeddingProviderMutation();
    // const setEnabledVectorStoreProviderMutation = useSetEnabledVectorStoreProviderMutation();
    const setEnabledAuthProviderMutation = useSetEnabledAuthProviderMutation();

    // Chat provider action callbacks
    const chatProviderActions = {
        onViewDetails: (provider: any) => {
            setChatProviderDialog({ open: true, provider, mode: "edit" });
        },
        onEdit: (provider: any) => {
            setChatProviderDialog({ open: true, provider, mode: "edit" });
        },
        onToggleEnabled: (provider: any) => {
            setEnabledChatProviderMutation.mutate({
                id: provider.id,
                enabled: !provider.enabled,
            });
        },
        onDuplicate: (provider: any) => {
            // Create a copy with modified display name for duplication
            const duplicateProvider = {
                ...provider,
                display_name: `${provider.display_name} (Copy)`,
            };
            setChatProviderDialog({ open: true, provider: duplicateProvider, mode: "create" });
        },
    };

    // Action callbacks for embedding providers
    const embeddingProviderActions = {
        onViewDetails: (provider: any) => {
            setEmbeddingProviderDialog({ open: true, provider, mode: "edit" });
        },
        onEdit: (provider: any) => {
            setEmbeddingProviderDialog({ open: true, provider, mode: "edit" });
        },
        onToggleEnabled: (provider: any) => {
            setEnabledEmbeddingProviderMutation.mutate({
                id: provider.id,
                enabled: !provider.enabled,
            });
        },
        onDuplicate: (provider: any) => {
            // Create a copy with modified display name for duplication
            const duplicateProvider = {
                ...provider,
                display_name: `${provider.display_name} (Copy)`,
            };
            setEmbeddingProviderDialog({ open: true, provider: duplicateProvider, mode: "create" });
        },
    };

    // Action callbacks for auth providers
    const authProviderActions = {
        onViewDetails: (provider: any) => {
            setAuthProviderDialog({ open: true, provider });
        },
        onEdit: (provider: any) => {
            setAuthProviderDialog({ open: true, provider });
        },
        onToggleEnabled: (provider: any) => {
            setEnabledAuthProviderMutation.mutate({
                id: provider.id,
                enabled: !provider.enabled,
            });
        },
    };

    const renderContent = () => {
        switch (setting) {
            case "chat":
                return (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <h2 className="text-2xl font-bold">{t("settings.models.title")}</h2>
                            <p className="text-base text-gray-500">{t("settings.models.description")}</p>
                        </div>
                        {chatProvidersQuery.isLoading ? (
                            <DataTableSkeleton
                                rowCount={5}
                                columnCount={5}
                                searchableColumnCount={2}
                                filterableColumnCount={0}
                                extraButtonsCount={1}
                            />
                        ) : (
                            <ChatProviderDataTable data={chatProvidersQuery.data || []} actions={chatProviderActions} />
                        )}
                    </div>
                );

            case "embedding":
                return (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <h2 className="text-2xl font-bold">{t("settings.embeddingProviders.title")}</h2>
                            <p className="text-base text-gray-500">{t("settings.embeddingProviders.description")}</p>
                        </div>
                        {embeddingProvidersQuery.isLoading ? (
                            <DataTableSkeleton
                                rowCount={5}
                                columnCount={5}
                                searchableColumnCount={2}
                                filterableColumnCount={0}
                                extraButtonsCount={1}
                            />
                        ) : (
                            <EmbeddingProviderDataTable
                                data={embeddingProvidersQuery.data || []}
                                actions={embeddingProviderActions}
                            />
                        )}
                    </div>
                );

            case "auth":
                return (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <h2 className="text-2xl font-bold">{t("settings.authProviders.title")}</h2>
                            <p className="text-base text-gray-500">
                                {t("settings.authProviders.description")}
                            </p>
                        </div>
                        {authProvidersQuery.isLoading ? (
                            <DataTableSkeleton
                                rowCount={5}
                                columnCount={5}
                                searchableColumnCount={2}
                                filterableColumnCount={0}
                                extraButtonsCount={1}
                            />
                        ) : (
                            <AuthProviderDataTable data={authProvidersQuery.data || []} actions={authProviderActions} />
                        )}
                    </div>
                );

            case "application":
                return (
                    <div className="space-y-6">
                        <ApplicationSettingsSection />
                    </div>
                );

            case "branding":
                return (
                    <div className="space-y-6">
                        <BrandingSettingsSection />
                    </div>
                );

            case "smtp":
                return (
                    <div className="space-y-6">
                        <SmtpSettingsSection />
                    </div>
                );

            case "notifications":
                return (
                    <div className="space-y-6">
                        <NotificationSettingsSection />
                    </div>
                );

            case "ssl":
                return (
                    <div className="space-y-6">
                        <SSLSettingsSection />
                    </div>
                );

            case "personalization":
                return (
                    <div className="space-y-6">
                        <Personalization />
                    </div>
                );

            default:
                return (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <h2 className="text-2xl font-bold">{t("settings.models.title")}</h2>
                            <p className="text-base text-gray-500">{t("settings.models.description")}</p>
                        </div>
                        {chatProvidersQuery.isLoading ? (
                            <DataTableSkeleton
                                rowCount={5}
                                columnCount={5}
                                searchableColumnCount={2}
                                filterableColumnCount={0}
                                extraButtonsCount={1}
                            />
                        ) : (
                            <ChatProviderDataTable data={chatProvidersQuery.data || []} actions={chatProviderActions} />
                        )}
                    </div>
                );
        }
    };

    return (
        <div className="container flex-1 flex-col flex-wrap items-center px-2 pb-8 md:px-4 md:pt-2 lg:px-4">
            <div className="px-1 pt-3">
                {renderContent()}
            </div>

            {/* Dialogs */}
            <ChatProviderDialog
                open={chatProviderDialog.open}
                onOpenChange={(open) => setChatProviderDialog((prev) => ({ ...prev, open }))}
                provider={chatProviderDialog.provider}
                mode={chatProviderDialog.mode}
            />

            <EmbeddingProviderDialog
                open={embeddingProviderDialog.open}
                onOpenChange={(open) => setEmbeddingProviderDialog((prev) => ({ ...prev, open }))}
                provider={embeddingProviderDialog.provider}
                mode={embeddingProviderDialog.mode}
            />

            <VectorStoreProviderDialog
                open={vectorStoreProviderDialog.open}
                onOpenChange={(open) => setVectorStoreProviderDialog((prev) => ({ ...prev, open }))}
                provider={vectorStoreProviderDialog.provider}
            />

            <AuthProviderDialog
                open={authProviderDialog.open}
                onOpenChange={(open) => setAuthProviderDialog((prev) => ({ ...prev, open }))}
                provider={authProviderDialog.provider}
            />
        </div>
    );
};

export default Settings;
